import { ResponsiveTable } from '../components/ResponsiveTable';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit,
  Eye,
  Filter,
  FlaskConical,
  Loader2,
  MinusCircle,
  Play,
  Plus,
  Search,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useToast } from '../components/Toast';
import { ConfirmModal } from '../components/ConfirmModal';
import { ConfirmSoftDeleteModal } from '../components/ConfirmSoftDeleteModal';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { RootState } from '../store';
import {
  deleteInstance,
  getInstances,
  INSTANCE_STATUS_BADGE,
  TEST_MODULES,
  TestCaseInstance,
  TestEnvironment,
  TestInstanceStatus,
} from '../services/testCasesApi';

// ── Constants ──────────────────────────────────────────────────────────────────

const INSTANCE_STATUS_LABEL: Record<TestInstanceStatus, string> = {
  NOT_RUN: 'Non exécuté',
  IN_PROGRESS: 'En cours',
  PASSED: 'Réussi',
  FAILED: 'Échoué',
  PARTIAL: 'Partiel',
  BLOCKED: 'Bloqué',
};

const ENV_LABEL: Record<TestEnvironment, string> = {
  LOCAL: 'Local',
  STAGING: 'Staging',
  PRODUCTION: 'Production',
};

const ENV_BADGE: Record<TestEnvironment, string> = {
  LOCAL: 'bg-gray-100 text-gray-700',
  STAGING: 'bg-blue-100 text-blue-800',
  PRODUCTION: 'bg-purple-100 text-purple-800',
};

type SortField = 'date' | 'testCase' | 'status' | 'environment' | 'executorName';

function sortValue(inst: TestCaseInstance, field: SortField): string | number {
  switch (field) {
    case 'date': return inst.date ? new Date(inst.date).getTime() : 0;
    case 'testCase': return inst.testCase?.name?.toLowerCase() ?? '';
    case 'status': return inst.status;
    case 'environment': return inst.environment;
    case 'executorName': return inst.executorName?.toLowerCase() ?? '';
    default: return '';
  }
}

// ── Component ──────────────────────────────────────────────────────────────────

export function TestCaseInstancesListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast, ToastComponent } = useToast();
  const { user } = useSelector((state: RootState) => state.auth);
  const canHardDelete = user?.role === 'super_admin';

  const [instances, setInstances] = useState<TestCaseInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TestInstanceStatus | ''>(
    (searchParams.get('status') as TestInstanceStatus) || '',
  );
  const [envFilter, setEnvFilter] = useState<TestEnvironment | ''>(
    (searchParams.get('environment') as TestEnvironment) || '',
  );
  const [moduleFilter, setModuleFilter] = useState(searchParams.get('moduleId') || '');

  const [sortKey, setSortKey] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkSoftDeleting, setBulkSoftDeleting] = useState(false);
  const [bulkHardDeleting, setBulkHardDeleting] = useState(false);
  const [bulkSoftDeleteConfirm, setBulkSoftDeleteConfirm] = useState<{ open: boolean; ids: string[] }>({
    open: false,
    ids: [],
  });
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{ open: boolean; ids: string[] }>({
    open: false,
    ids: [],
  });

  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string }>({
    open: false, id: '', name: '',
  });
  const [singleDeleting, setSingleDeleting] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getInstances({
        status: statusFilter || undefined,
        environment: envFilter || undefined,
      });
      setInstances(data);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter, envFilter]);

  const sorted = useMemo(() => {
    let list = instances;

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (i) =>
          i.testCase?.name?.toLowerCase().includes(q) ||
          i.executorName?.toLowerCase().includes(q) ||
          i.generalNotes?.toLowerCase().includes(q),
      );
    }

    if (moduleFilter) {
      list = list.filter((i) => i.testCase?.moduleId === moduleFilter);
    }

    return [...list].sort((a, b) => {
      const va = sortValue(a, sortKey);
      const vb = sortValue(b, sortKey);
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [instances, search, moduleFilter, sortKey, sortDir]);

  const handleSort = (key: SortField) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleDelete = async () => {
    try {
      setSingleDeleting(true);
      await deleteInstance(deleteConfirm.id);
      showToast('Exécution archivée', 'success');
      setInstances((prev) => prev.filter((i) => i.id !== deleteConfirm.id));
      setSelected((prev) => { const n = new Set(prev); n.delete(deleteConfirm.id); return n; });
    } catch (err: any) {
      showToast(err.message || 'Erreur lors de la suppression', 'error');
    } finally {
      setSingleDeleting(false);
      setDeleteConfirm({ open: false, id: '', name: '' });
    }
  };

  const runBulkDelete = async (ids: string[], hardDelete: boolean) => {
    const failed: string[] = [];
    for (const id of ids) {
      try {
        await deleteInstance(id, { hardDelete });
      } catch {
        failed.push(id);
      }
    }
    const deletedIds = ids.filter((id) => !failed.includes(id));
    setInstances((prev) => prev.filter((i) => !deletedIds.includes(i.id)));
    setSelected((prev) => {
      const next = new Set(prev);
      deletedIds.forEach((id) => next.delete(id));
      return next;
    });
    return { deletedCount: deletedIds.length, failedCount: failed.length };
  };

  const handleBulkSoftDelete = async () => {
    const ids = bulkSoftDeleteConfirm.ids;
    if (!ids.length) return;
    try {
      setBulkSoftDeleting(true);
      const { deletedCount, failedCount } = await runBulkDelete(ids, false);
      setBulkSoftDeleteConfirm({ open: false, ids: [] });
      if (failedCount === 0) {
        showToast(
          `${deletedCount} exécution${deletedCount > 1 ? 's' : ''} archivée${deletedCount > 1 ? 's' : ''}`,
          'success',
        );
      } else if (deletedCount === 0) {
        showToast('Aucune exécution archivée', 'error');
      } else {
        showToast(`${deletedCount} archivée(s), ${failedCount} échec(s)`, 'error', 7000);
      }
    } finally {
      setBulkSoftDeleting(false);
    }
  };

  const handleBulkHardDelete = async () => {
    const ids = bulkHardDeleteConfirm.ids;
    if (!ids.length) return;
    try {
      setBulkHardDeleting(true);
      const { deletedCount, failedCount } = await runBulkDelete(ids, true);
      setBulkHardDeleteConfirm({ open: false, ids: [] });
      if (failedCount === 0) {
        showToast(
          `${deletedCount} exécution${deletedCount > 1 ? 's' : ''} supprimée${deletedCount > 1 ? 's' : ''} définitivement`,
          'success',
        );
      } else if (deletedCount === 0) {
        showToast('Aucune exécution supprimée', 'error');
      } else {
        showToast(`${deletedCount} supprimée(s), ${failedCount} échec(s)`, 'error', 7000);
      }
    } finally {
      setBulkHardDeleting(false);
    }
  };

  const openBulkSoftDelete = () => {
    if (selected.size === 0) return;
    setBulkSoftDeleteConfirm({ open: true, ids: [...selected] });
  };

  const openBulkHardDelete = () => {
    if (selected.size === 0) return;
    setBulkHardDeleteConfirm({ open: true, ids: [...selected] });
  };

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const toggleSelectAll = () => {
    if (sorted.length > 0 && sorted.every((i) => selected.has(i.id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sorted.map((i) => i.id)));
    }
  };

  const allSelected = sorted.length > 0 && sorted.every((i) => selected.has(i.id));
  const someSelected = selected.size > 0 && !allSelected;

  const SortIcon = ({ field }: { field: SortField }) =>
    sortKey === field ? (
      sortDir === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />
    ) : null;

  const passRate = useMemo(() => {
    const done = instances.filter((i) => i.status === 'PASSED' || i.status === 'FAILED');
    if (!done.length) return null;
    return Math.round((instances.filter((i) => i.status === 'PASSED').length / done.length) * 100);
  }, [instances]);

  const statusCounts = useMemo(() => {
    const counts: Partial<Record<TestInstanceStatus, number>> = {};
    for (const i of instances) {
      counts[i.status] = (counts[i.status] ?? 0) + 1;
    }
    return counts;
  }, [instances]);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      <ToastComponent />

      {/* Header */}
      <div className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 px-4 py-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-amber-100 flex-shrink-0">
            <Play className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Exécutions de tests</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {instances.length} exécution{instances.length !== 1 ? 's' : ''} au total
              {sorted.length !== instances.length ? ` · ${sorted.length} affiché${sorted.length > 1 ? 's' : ''}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate('/test-cases')}
            className="btn-secondary inline-flex items-center gap-1.5 text-sm"
          >
            <FlaskConical className="w-3.5 h-3.5" />
            Cas de test
          </button>
          <button
            onClick={() => navigate('/test-cases')}
            className="btn-primary inline-flex items-center gap-1.5 text-sm"
          >
            <Plus className="w-3.5 h-3.5" />
            Nouvelle exécution
          </button>
        </div>
      </div>

      {/* Stats strip */}
      {instances.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-2">
          {(Object.entries(INSTANCE_STATUS_LABEL) as [TestInstanceStatus, string][]).map(([status, label]) => (
            <button
              key={status}
              onClick={() => setStatusFilter((prev) => (prev === status ? '' : status))}
              className={`flex flex-col items-center justify-center p-2 rounded-lg border text-center cursor-pointer transition-all ${
                statusFilter === status
                  ? 'ring-2 ring-amber-400 border-amber-400'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${INSTANCE_STATUS_BADGE[status]}`}>
                {statusCounts[status] ?? 0}
              </span>
              <span className="text-xs text-gray-500 mt-1">{label}</span>
            </button>
          ))}
          {passRate !== null && (
            <div className="flex flex-col items-center justify-center p-2 rounded-lg border border-gray-200 bg-green-50 text-center">
              <span className="text-lg font-bold text-green-700">{passRate}%</span>
              <span className="text-xs text-gray-500">Taux de réussite</span>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="dt-filters bg-white rounded-lg border border-gray-200 p-3 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher (cas de test, exécuteur…)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-400"
          />
        </div>
        <select
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
        >
          <option value="">Tous les modules</option>
          {TEST_MODULES.map((m) => (
            <option key={m.id} value={m.id}>{m.label}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as TestInstanceStatus | '')}
          className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
        >
          <option value="">Tous les statuts</option>
          {(Object.entries(INSTANCE_STATUS_LABEL) as [TestInstanceStatus, string][]).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select
          value={envFilter}
          onChange={(e) => setEnvFilter(e.target.value as TestEnvironment | '')}
          className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
        >
          <option value="">Tous les environnements</option>
          {(Object.entries(ENV_LABEL) as [TestEnvironment, string][]).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        {(search || statusFilter || envFilter || moduleFilter) && (
          <button
            onClick={() => { setSearch(''); setStatusFilter(''); setEnvFilter(''); setModuleFilter(''); }}
            className="text-xs text-amber-600 hover:text-amber-800 font-medium"
          >
            Réinitialiser
          </button>
        )}
        <span className="text-xs text-gray-400 ml-auto">{sorted.length} résultat{sorted.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex-wrap">
          <span className="text-sm font-medium text-amber-800">
            {selected.size} sélectionné{selected.size > 1 ? 'es' : 'e'}
          </span>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <button
              onClick={openBulkSoftDelete}
              disabled={bulkSoftDeleting || bulkHardDeleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-800 bg-amber-100 border border-amber-300 hover:bg-amber-200 rounded-md disabled:opacity-50"
            >
              Archiver ({selected.size})
            </button>
            {canHardDelete && (
            <button
              onClick={openBulkHardDelete}
              disabled={bulkSoftDeleting || bulkHardDeleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 rounded-md disabled:opacity-50"
            >
              <Trash2 className="w-3 h-3" />
              Supprimer définitivement ({selected.size})
            </button>
            )}
            <button
              onClick={() => setSelected(new Set())}
              disabled={bulkSoftDeleting || bulkHardDeleting}
              className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              Désélectionner
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
          <span className="ml-2 text-sm text-gray-500">Chargement…</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && sorted.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Play className="w-10 h-10 text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">Aucune exécution trouvée</p>
          <p className="text-gray-400 text-sm mt-1">
            Ouvrez un cas de test et cliquez sur "Nouvelle exécution" pour commencer.
          </p>
          <button
            onClick={() => navigate('/test-cases')}
            className="mt-4 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100"
          >
            <FlaskConical className="w-4 h-4" />
            Voir les cas de test
          </button>
        </div>
      )}

      {/* Table */}
      {!loading && sorted.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <ResponsiveTable className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="pl-4 pr-2 py-2.5 w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-amber-500 focus:ring-amber-400 cursor-pointer"
                    />
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('date')}
                  >
                    <span className="flex items-center">Date <SortIcon field="date" /></span>
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('testCase')}
                  >
                    <span className="flex items-center">Cas de test <SortIcon field="testCase" /></span>
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('executorName')}
                  >
                    <span className="flex items-center">Exécuteur <SortIcon field="executorName" /></span>
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('environment')}
                  >
                    <span className="flex items-center">Environnement <SortIcon field="environment" /></span>
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Étapes
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <span className="flex items-center">Statut <SortIcon field="status" /></span>
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorted.map((inst) => {
                  const passed = inst.stepResults.filter((s) => s.status === 'passed').length;
                  const failed = inst.stepResults.filter((s) => s.status === 'failed').length;
                  const total = inst.stepResults.length;
                  const module = TEST_MODULES.find((m) => m.id === inst.testCase?.moduleId);

                  return (
                    <tr
                      key={inst.id}
                      className={`hover:bg-amber-50 cursor-pointer transition-colors ${selected.has(inst.id) ? 'bg-amber-50' : ''}`}
                      onClick={() => navigate(`/test-case-instances/${inst.id}`)}
                    >
                      <td className="pl-4 pr-2 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(inst.id)}
                          onChange={() => toggleSelect(inst.id)}
                          className="rounded border-gray-300 text-amber-500 focus:ring-amber-400 cursor-pointer"
                        />
                      </td>
                      {/* Date */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-800">
                          {inst.date
                            ? new Date(inst.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
                            : <span className="text-gray-400">—</span>}
                        </div>
                        {(inst.startTime || inst.endTime) && (
                          <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                            <Clock className="w-3 h-3" />
                            {inst.startTime && <span>{inst.startTime}</span>}
                            {inst.startTime && inst.endTime && <span>→</span>}
                            {inst.endTime && <span>{inst.endTime}</span>}
                          </div>
                        )}
                      </td>

                      {/* Test case */}
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800 text-sm line-clamp-1">
                          {inst.testCase?.name ?? <span className="text-gray-400 italic">Cas supprimé</span>}
                        </div>
                        {module && (
                          <div className="text-xs text-gray-400 mt-0.5">{module.label}</div>
                        )}
                      </td>

                      {/* Executor */}
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-700">{inst.executorName || <span className="text-gray-400">—</span>}</div>
                        {inst.executorRole && (
                          <div className="text-xs text-gray-400">{inst.executorRole}</div>
                        )}
                      </td>

                      {/* Environment */}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ENV_BADGE[inst.environment]}`}>
                          {ENV_LABEL[inst.environment]}
                        </span>
                      </td>

                      {/* Steps mini-bar */}
                      <td className="px-4 py-3">
                        {total > 0 ? (
                          <div className="flex items-center gap-1.5">
                            <div className="flex gap-0.5 h-2 w-20 rounded overflow-hidden bg-gray-100">
                              {inst.stepResults.map((s, idx) => (
                                <div
                                  key={idx}
                                  className={`flex-1 ${
                                    s.status === 'passed' ? 'bg-green-500' :
                                    s.status === 'failed' ? 'bg-red-500' :
                                    s.status === 'skipped' ? 'bg-gray-400' : 'bg-gray-200'
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-xs text-gray-500">
                              {passed > 0 && <span className="text-green-600 font-medium">{passed}✓</span>}
                              {failed > 0 && <span className="text-red-600 font-medium ml-1">{failed}✗</span>}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${INSTANCE_STATUS_BADGE[inst.status]}`}>
                          {inst.status === 'PASSED' && <CheckCircle2 className="w-3 h-3" />}
                          {inst.status === 'FAILED' && <XCircle className="w-3 h-3" />}
                          {inst.status === 'BLOCKED' && <MinusCircle className="w-3 h-3" />}
                          {INSTANCE_STATUS_LABEL[inst.status]}
                        </span>
                        {inst.issues.length > 0 && (
                          <div className="text-xs text-red-500 mt-0.5">{inst.issues.length} incident{inst.issues.length > 1 ? 's' : ''}</div>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/test-case-instances/${inst.id}`)}
                            title="Voir"
                            className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => navigate(`/test-case-instances/${inst.id}/edit`)}
                            title="Modifier"
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm({ open: true, id: inst.id, name: inst.testCase?.name ?? inst.id })}
                            title="Supprimer"
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </ResponsiveTable>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirm.open}
        title="Archiver l'exécution"
        message={`Archiver cette exécution du cas « ${deleteConfirm.name} » ? Elle sera masquée de la liste mais conservée en base.`}
        confirmLabel="Archiver"
        cancelLabel="Annuler"
        confirmVariant="warning"
        isLoading={singleDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirm({ open: false, id: '', name: '' })}
      />

      <ConfirmSoftDeleteModal
        isOpen={bulkSoftDeleteConfirm.open}
        onConfirm={handleBulkSoftDelete}
        onClose={() => setBulkSoftDeleteConfirm({ open: false, ids: [] })}
        entityLabel={`exécution${bulkSoftDeleteConfirm.ids.length > 1 ? 's' : ''} de test`}
        count={bulkSoftDeleteConfirm.ids.length}
        isLoading={bulkSoftDeleting}
      />

      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.open}
        onConfirm={handleBulkHardDelete}
        onClose={() => setBulkHardDeleteConfirm({ open: false, ids: [] })}
        entityLabel={`exécution${bulkHardDeleteConfirm.ids.length > 1 ? 's' : ''} de test`}
        count={bulkHardDeleteConfirm.ids.length}
        isLoading={bulkHardDeleting}
      />
    </div>
  );
}
